import { createAsyncThunk, unwrapResult } from '@reduxjs/toolkit';
import axios from 'axios';
import { formatError, subscriptionRequestsApi } from '@/services/api';
import {
  Subscription,
  SubscriptionDetail,
} from '@/types/subscriptions/subscription';
import { ServiceGroup } from '@/types/serviceGroup';
import { organizationAffiliationStore } from '@/store/organization/affiliation';
import { organizationContactStore } from '@/store/organization/contact';
import { organizationBillingContactStore } from '@/store/organization/billingContact';
import { organizationProjectClassStore } from '@/store/organization/projectClass';
import { organizationServiceStore } from '@/store/organization/service';
import { handleError } from '@/utils/apiErrorHandler';
import { FlashMessage } from '@/types/flashMessage';
import { flashMessageStore } from '@/store/flashMessage';
import { SubscriptionRequest } from '@/types/subscriptions/request';

import {
  normalizeCreatePayload,
  normalizeSubscriptionRequestUpdatePayload,
} from './utils';

import { RootState } from '@/store';
import { pages } from '@/settings/pages';

type GetPayload = {
  requestCode?: Subscription['requestCode'];
};

type InitializeSubscriptionNewPayload = {
  serviceGroupId: ServiceGroup['id'];
};

type InitializeSubscriptionEditPayload = {
  requestCode?: Subscription['requestCode'];
};

type CreatePayload = {
  subscriptionRequest: SubscriptionRequest;
  dryRun: boolean;
};

type UpdatePayload = {
  requestCode?: SubscriptionDetail['requestCode'];
  subscriptionRequest: SubscriptionRequest;
  snapshots: {
    contactId?: number;
    billingContactId?: number;
  };
  dryRun: boolean;
};

export const get = createAsyncThunk(
  'organization-subscription/get',
  async (params: GetPayload, { getState, rejectWithValue }) => {
    const rootState = getState() as RootState;
    const organizationId = rootState.auth.current.organization.id;
    try {
      const response = await subscriptionRequestsApi.get({
        data: {
          organizationId,
          requestCode: params.requestCode,
        },
      });

      return response;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response !== undefined) {
        const isNotFound = error.response && error.response.status === 404;
        if (isNotFound) window.location.assign(pages.top);
        return rejectWithValue({ error: formatError(error.response) });
      }
      throw error;
    }
  },
);

export const initializeSubscriptionNew = createAsyncThunk(
  'organization-subscription/initializeSubscriptionNew',
  async (
    params: InitializeSubscriptionNewPayload,
    { rejectWithValue, dispatch },
  ) => {
    try {
      await dispatch(organizationServiceStore.asyncActions.fetchList(params));

      await Promise.all([
        dispatch(organizationAffiliationStore.asyncActions.fetchAll()),
        dispatch(organizationContactStore.asyncActions.fetchAll()),
        dispatch(organizationBillingContactStore.asyncActions.fetchAll()),
        dispatch(organizationProjectClassStore.asyncActions.fetchBundledList()),
      ]);
      return;
    } catch (error) {
      handleError((message: FlashMessage) => {
        dispatch(flashMessageStore.actions.set(message));
      }, error);
      if (axios.isAxiosError(error) && error.response !== undefined) {
        return rejectWithValue({ error: formatError(error.response) });
      }
      throw error;
    }
  },
);

export const initializeSubscriptionEdit = createAsyncThunk(
  'organization-subscription/initializeSubscriptionEdit',
  async (
    params: InitializeSubscriptionEditPayload,
    { rejectWithValue, dispatch },
  ) => {
    const { requestCode } = params;
    let serviceGroupId: number | undefined;
    try {
      await dispatch(get({ requestCode }))
        .then(unwrapResult)
        .then((subscriptionDetail) => {
          serviceGroupId = subscriptionDetail?.services[0]?.serviceGroup?.id;
        });

      await Promise.all([
        dispatch(organizationAffiliationStore.asyncActions.fetchAll()),
        dispatch(organizationContactStore.asyncActions.fetchAll()),
        dispatch(organizationBillingContactStore.asyncActions.fetchAll()),
        dispatch(organizationProjectClassStore.asyncActions.fetchBundledList()),
        serviceGroupId &&
          dispatch(
            organizationServiceStore.asyncActions.fetchList({
              serviceGroupId,
            }),
          ),
      ]);
      return;
    } catch (error) {
      handleError((message: FlashMessage) => {
        dispatch(flashMessageStore.actions.set(message));
      }, error);
      if (axios.isAxiosError(error) && error.response !== undefined) {
        return rejectWithValue({ error: formatError(error.response) });
      }
      throw error;
    }
  },
);

export const create = createAsyncThunk(
  'organization-subscription/create',
  async (params: CreatePayload, { rejectWithValue, getState }) => {
    const subscriptionRequest = normalizeCreatePayload(
      params.subscriptionRequest,
    );

    const normalizedParams = {
      dryRun: params.dryRun,
      subscriptionRequest,
    };
    try {
      const rootState = getState() as RootState;
      const organizationId = rootState.auth.current.organization.id;

      const { subscription, warnings } = await subscriptionRequestsApi.create({
        data: {
          organizationId,
          subscriptionRequest,
        },
        params: { dryrun: normalizedParams.dryRun },
      });
      return { item: subscription, warnings };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response !== undefined) {
        return rejectWithValue({ error: formatError(error.response) });
      }
      throw error;
    }
  },
);

export const update = createAsyncThunk(
  'organization-subscription/update',
  async (params: UpdatePayload, { rejectWithValue, getState }) => {
    const rootState = getState() as RootState;
    const organizationId = rootState.auth.current.organization.id;
    const subscriptionRequest = normalizeSubscriptionRequestUpdatePayload(
      params.subscriptionRequest,
      params.snapshots,
    );

    const normalizedParams = {
      requestCode: params.requestCode,
      dryRun: params.dryRun,
      snapshots: params.snapshots,
      subscriptionRequest,
    };
    try {
      const { subscription, warnings } = await subscriptionRequestsApi.update({
        data: {
          organizationId,
          requestCode: normalizedParams.requestCode,
          subscriptionRequest,
        },
        params: { dryrun: normalizedParams.dryRun },
      });
      return {
        item: subscription,
        warnings,
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response !== undefined) {
        // FIXME: 申込移管リリース後削除したい
        if (error.response && error.response.status === 404) {
          return rejectWithValue({
            error: formatError(error.response),
            isNotFound: true,
          });
        }
        return rejectWithValue({ error: formatError(error.response) });
      }
      throw error;
    }
  },
);
